/**
 * Headless document uploads for the file manager: `useDocumentUploads` opens
 * the native file picker (or takes dropped files) and posts them to the
 * workspace upload route; `UploadStatusPanel` shows per-file progress in a
 * floating card. The old always-visible dropzone is gone — the list area
 * shows a drop overlay only while the user is actually dragging files in.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { FileText, Check, X, Loader2, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  DOCUMENT_UPLOAD_EXTENSIONS,
  TRANSCRIBABLE_EXTENSIONS,
  fileExtensionOf,
  maxDocumentBytes,
} from '@hub/contracts'
import { toApiUrl } from '@/lib/orpc-client'
import { transcriptionAvailable } from '@/lib/health-features'
import { cn } from '@/lib/utils'

export interface FileUploadState {
  name: string
  status: 'uploading' | 'done' | 'error' | 'rejected'
  error?: string
}

/** Office files, text and pictures (board card 2u86) — the upload route's own list. */
export const ACCEPTED_TYPES = [...DOCUMENT_UPLOAD_EXTENSIONS]
  .filter((ext) => !TRANSCRIBABLE_EXTENSIONS.has(ext))
  .join(',')
/** Audio/video too, once the Worker reports an async STT provider (board card loap). */
export const ACCEPTED_TYPES_WITH_AUDIO = [...DOCUMENT_UPLOAD_EXTENSIONS].join(',')

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

export function useDocumentUploads(projectId: string, onUploadComplete: () => void) {
  const { t } = useTranslation('documents')
  const [items, setItems] = useState<FileUploadState[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  /** Audio/video uploads become transcripts — only offered when the Worker can transcribe. */
  const [transcription, setTranscription] = useState(false)
  useEffect(() => {
    void transcriptionAvailable().then(setTranscription)
  }, [])
  const acceptedTypes = transcription ? ACCEPTED_TYPES_WITH_AUDIO : ACCEPTED_TYPES

  const uploadFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const allFiles = Array.from(fileList)
      if (!allFiles.length) return

      const accepted = new Set(acceptedTypes.split(','))
      const valid: File[] = []
      const entries: FileUploadState[] = []
      for (const file of allFiles) {
        const ext = fileExtensionOf(file.name)
        // Pictures: 20 MB (the Images binding's cap); everything else 50 MB.
        const maxBytes = maxDocumentBytes(ext)
        if (!accepted.has(ext)) {
          entries.push({
            name: file.name,
            status: 'rejected',
            error: t(transcription ? 'upload.unsupportedTypeWithAudio' : 'upload.unsupportedType', {
              ext: ext || t('upload.unknownType'),
            }),
          })
        } else if (file.size > maxBytes) {
          entries.push({
            name: file.name,
            status: 'rejected',
            error: t('upload.tooLarge', {
              size: formatFileSize(file.size),
              max: formatFileSize(maxBytes),
            }),
          })
        } else {
          valid.push(file)
          entries.push({ name: file.name, status: 'uploading' })
        }
      }
      setItems(entries)
      if (!valid.length) return

      setIsUploading(true)
      const formData = new FormData()
      for (const file of valid) formData.append('files', file)
      try {
        const res = await fetch(toApiUrl(`/workspaces/${projectId}/documents/upload`), {
          method: 'POST',
          body: formData,
          credentials: 'include',
        })
        setItems((prev) =>
          prev.map((f) =>
            f.status === 'uploading'
              ? res.ok
                ? { ...f, status: 'done' }
                : { ...f, status: 'error', error: t('upload.failed') }
              : f
          )
        )
        if (res.ok) onUploadComplete()
      } catch {
        setItems((prev) =>
          prev.map((f) =>
            f.status === 'uploading'
              ? { ...f, status: 'error', error: t('upload.networkError') }
              : f
          )
        )
      } finally {
        setIsUploading(false)
      }
    },
    [projectId, onUploadComplete, t, acceptedTypes, transcription]
  )

  const openPicker = useCallback(() => inputRef.current?.click(), [])
  const clear = useCallback(() => setItems([]), [])

  /** Render this once near the page root. */
  const input = (
    <input
      ref={inputRef}
      type="file"
      multiple
      accept={acceptedTypes}
      className="hidden"
      onChange={(e) => {
        if (e.target.files?.length) void uploadFiles(e.target.files)
        e.target.value = ''
      }}
    />
  )

  return { items, isUploading, uploadFiles, openPicker, clear, input, transcription }
}

/** Floating per-file status card (bottom right) while uploads are in flight. */
export function UploadStatusPanel({
  items,
  isUploading,
  onDismiss,
}: {
  items: FileUploadState[]
  isUploading: boolean
  onDismiss: () => void
}) {
  const { t } = useTranslation('documents')
  if (items.length === 0) return null
  return (
    <div className="fixed right-6 bottom-6 z-40 w-80 rounded-xl border border-border bg-popover p-3 shadow-lg">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold text-foreground/70">
          {isUploading ? t('upload.uploading') : t('upload.uploadDone')}
        </p>
        {!isUploading && (
          <button
            type="button"
            aria-label={t('upload.dismiss')}
            onClick={onDismiss}
            className="rounded p-1 text-muted-foreground hover:bg-accent"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="space-y-1">
        {items.map((f, i) => (
          <div
            key={i}
            className={cn(
              'flex items-center gap-2 rounded px-2 py-1.5 text-sm',
              f.status === 'rejected' ? 'bg-destructive/10' : 'bg-muted/50'
            )}
          >
            <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <span className="block truncate">{f.name}</span>
              {f.error && <span className="block text-xs text-destructive">{f.error}</span>}
            </div>
            {f.status === 'uploading' && (
              <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-muted-foreground" />
            )}
            {f.status === 'done' && <Check className="h-4 w-4 flex-shrink-0 text-green-500" />}
            {f.status === 'error' && <X className="h-4 w-4 flex-shrink-0 text-destructive" />}
            {f.status === 'rejected' && (
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-destructive" />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
